import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  type AppUser,
  type MinAktivitet,
  type MyBoat,
  type MySeilas,
} from '../../lib/api.ts';
import { SiteLayout } from '../../components/SiteLayout.tsx';
import { ActivityMap } from '../../components/ActivityMap.tsx';

type Tab = 'baater' | 'seilaser' | 'aktiviteter' | 'innstillinger';

export function MinSide() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['konto-me'], queryFn: () => api.kontoMe(), retry: false });
  const [tab, setTab] = useState<Tab>('baater');

  if (me.isLoading) {
    return (
      <SiteLayout>
        <div className="max-w-2xl mx-auto px-4 py-6 text-slate-400">Laster…</div>
      </SiteLayout>
    );
  }
  if (me.isError || !me.data) return <Navigate to="/logg-inn" replace />;

  async function logout() {
    try {
      await api.kontoLogout();
    } catch {
      /* ignorer */
    }
    qc.removeQueries({ queryKey: ['konto-me'] });
    nav('/logg-inn', { replace: true });
  }

  const tabClass = (t: Tab) =>
    `flex-1 text-center py-3 text-xs sm:text-sm font-medium -mb-px ${
      tab === t
        ? 'text-sky-300 border-b-2 border-sky-400'
        : 'text-slate-400 border-b-2 border-transparent'
    }`;

  return (
    <SiteLayout>
      <main className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h1 className="text-xl font-bold">Min side</h1>
            <p className="text-sm text-slate-400 truncate">
              {me.data.fullName}
              {me.data.foreningName && <span className="text-slate-500"> • {me.data.foreningName}</span>}
            </p>
          </div>
          <button
            onClick={logout}
            className="px-3 py-1.5 text-sm bg-slate-800 rounded-lg hover:bg-slate-700 shrink-0"
          >
            Logg ut
          </button>
        </div>
        {/* Mobil: korte navn; bredere skjerm: «Mine …». */}
        <nav className="flex border-b border-slate-800 mb-4">
          <button className={tabClass('baater')} onClick={() => setTab('baater')}>
            <span className="hidden sm:inline">Mine båter</span>
            <span className="sm:hidden">Båter</span>
          </button>
          <button className={tabClass('seilaser')} onClick={() => setTab('seilaser')}>
            <span className="hidden sm:inline">Mine seilaser</span>
            <span className="sm:hidden">Seilaser</span>
          </button>
          <button className={tabClass('aktiviteter')} onClick={() => setTab('aktiviteter')}>
            <span className="hidden sm:inline">Mine aktiviteter</span>
            <span className="sm:hidden">Aktiviteter</span>
          </button>
          <button className={tabClass('innstillinger')} onClick={() => setTab('innstillinger')}>
            Innstillinger
          </button>
        </nav>
        {tab === 'baater' && <BaaterSection />}
        {tab === 'seilaser' && <SeilaserSection />}
        {tab === 'aktiviteter' && <AktiviteterSection />}
        {tab === 'innstillinger' && <InnstillingerSection me={me.data} />}
      </main>
    </SiteLayout>
  );
}

// --- MINE AKTIVITETER (trening/tur logget i appen) ---

const TZ = 'Europe/Oslo';

function fmtAvstand(m: number | null): string {
  if (m == null) return '–';
  return m < 185 ? `${Math.round(m)} m` : `${(m / 1852).toFixed(1).replace('.', ',')} nm`;
}

function fmtVarighet(ms: number): string {
  const min = Math.max(0, Math.round(ms / 60_000));
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} t ${min % 60} min`;
}

function AktiviteterSection() {
  const q = useQuery({
    queryKey: ['bruker-aktiviteter'],
    queryFn: () => api.brukerAktiviteter(),
    refetchInterval: 30_000,
  });
  const [open, setOpen] = useState<string | null>(null);

  if (q.isLoading) return <p className="text-slate-500 text-sm">Laster…</p>;
  const list = q.data ?? [];
  if (list.length === 0) {
    return (
      <p className="text-slate-400 text-sm bg-slate-900/40 border border-dashed border-slate-800 rounded-xl p-4">
        Ingen aktiviteter ennå. Start en trening eller tur i Racecloud-appen (Instrument eller Start →
        Velg → Fri aktivitet), så havner den her med kart.
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {list.map((a) => (
        <AktivitetRad
          key={a.id}
          a={a}
          open={open === a.id}
          onToggle={() => setOpen(open === a.id ? null : a.id)}
        />
      ))}
    </ul>
  );
}

function AktivitetRad({ a, open, onToggle }: { a: MinAktivitet; open: boolean; onToggle: () => void }) {
  const pagar = !a.endedAt;
  const navn = a.type === 'tur' ? 'Tur' : 'Trening';
  const dato = new Date(a.startedAt).toLocaleString('no-NO', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  });
  const varighet = fmtVarighet((a.endedAt ? Date.parse(a.endedAt) : Date.now()) - Date.parse(a.startedAt));
  const meta = [
    `${a.sailNumber ?? ''} ${a.boatName ?? ''}`.trim(),
    varighet,
    pagar ? null : fmtAvstand(a.distanceM),
    a.maxSogKn ? `topp ${a.maxSogKn.toFixed(1).replace('.', ',')} kn` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <li className="bg-slate-900/40 border border-slate-800 rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full text-left p-3 flex items-center gap-3 hover:bg-slate-900/70">
        <span className="text-xl shrink-0" aria-hidden="true">
          {a.type === 'tur' ? '🧭' : '⛵'}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">
            {navn} · {dato}
          </div>
          <div className="text-xs text-slate-400 truncate">{meta}</div>
        </div>
        {pagar && (
          <span className="text-xs bg-emerald-600/30 text-emerald-300 px-2 py-0.5 rounded shrink-0">Pågår</span>
        )}
        <span className="text-slate-500 shrink-0">{open ? '▴' : '▾'}</span>
      </button>
      {open && <AktivitetDetalj a={a} navn={navn} tittel={[navn, dato, a.sailNumber].filter(Boolean).join(' · ')} />}
    </li>
  );
}

function AktivitetDetalj({ a, navn, tittel }: { a: MinAktivitet; navn: string; tittel: string }) {
  const qc = useQueryClient();
  const spor = useQuery({ queryKey: ['bruker-aktivitet', a.id], queryFn: () => api.brukerAktivitet(a.id) });
  const slett = useMutation({
    mutationFn: () => api.brukerSlettAktivitet(a.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bruker-aktiviteter'] }),
  });
  const pagar = !a.endedAt;

  return (
    <div className="border-t border-slate-800 p-3 space-y-3">
      {spor.isLoading ? (
        <p className="text-slate-500 text-sm">Laster spor…</p>
      ) : spor.data && spor.data.points.length > 0 ? (
        <ActivityMap points={spor.data.points} totalM={a.distanceM} tittel={tittel} />
      ) : (
        <p className="text-slate-500 text-sm">Ingen spor registrert for denne aktiviteten.</p>
      )}

      <div className="grid grid-cols-3 gap-2 text-center">
        <Nokkeltall label="Avstand" value={pagar ? 'beregnes ved slutt' : fmtAvstand(a.distanceM)} />
        <Nokkeltall
          label="Toppfart"
          value={a.maxSogKn ? `${a.maxSogKn.toFixed(1).replace('.', ',')} kn` : '–'}
        />
        <Nokkeltall label="Punkter" value={String(spor.data?.points.length ?? a.pointCount)} />
      </div>

      {slett.isError && (
        <p className="text-sm text-red-300">{(slett.error as ApiError).message}</p>
      )}
      {!pagar && (
        <div className="text-right">
          <button
            onClick={() => {
              if (confirm(`Slette ${navn.toLowerCase()}en? Sporet slettes også, og det kan ikke angres.`)) {
                slett.mutate();
              }
            }}
            disabled={slett.isPending}
            className="text-xs px-3 py-1.5 rounded border border-red-900 text-red-300 hover:bg-red-950/40 disabled:opacity-50"
          >
            {slett.isPending ? 'Sletter…' : 'Slett aktivitet'}
          </button>
        </div>
      )}
    </div>
  );
}

function Nokkeltall({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-950/50 border border-slate-800 rounded-lg p-2">
      <div className="text-sm font-semibold text-slate-100">{value}</div>
      <div className="text-[11px] text-slate-500">{label}</div>
    </div>
  );
}

// --- MINE BÅTER ---

function BaaterSection() {
  const qc = useQueryClient();
  const boats = useQuery({ queryKey: ['mine-baater'], queryFn: () => api.brukerBaater() });
  const [editing, setEditing] = useState<MyBoat | null>(null);
  const [showNew, setShowNew] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold">Mine båter</h1>
        <button
          className="bg-sky-600 hover:bg-sky-500 px-3 py-2 rounded text-sm font-semibold"
          onClick={() => {
            setShowNew(true);
            setEditing(null);
          }}
        >
          + Legg til båt
        </button>
      </div>

      {(showNew || editing) && (
        <BoatForm
          initial={editing ?? undefined}
          onClose={() => {
            setShowNew(false);
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['mine-baater'] });
          }}
        />
      )}

      {boats.isLoading ? (
        <p className="text-slate-500 text-sm">Laster…</p>
      ) : boats.data && boats.data.length > 0 ? (
        <ul className="divide-y divide-slate-800 border border-slate-800 rounded-xl overflow-hidden">
          {boats.data.map((b) => (
            <li key={b.id} className="p-3 bg-slate-900/30">
              <div className="flex items-center gap-3">
                <div className="font-mono text-xs w-16 text-slate-400">{b.sailNumber}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {b.boatName}
                    {b.className && (
                      <span className="text-xs bg-slate-700/40 text-slate-300 px-2 py-0.5 rounded">
                        {b.className}
                      </span>
                    )}
                    <span className="text-xs bg-slate-800 text-slate-400 px-2 py-0.5 rounded">
                      {b.role}
                    </span>
                  </div>
                  <div className="text-xs text-slate-400 truncate">
                    Skipper: {b.skipperName}
                    {b.crew && ` • Mannskap: ${b.crew}`}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setEditing(b);
                    setShowNew(false);
                  }}
                  className="text-xs text-sky-400"
                >
                  Rediger
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-center text-slate-500 text-sm p-6 border border-slate-800 rounded-xl">
          Du har ingen båter ennå. Legg til den første.
        </div>
      )}
    </div>
  );
}

function BoatForm({ initial, onClose }: { initial?: MyBoat; onClose: () => void }) {
  const qc = useQueryClient();
  const classes = useQuery({ queryKey: ['classes', false], queryFn: () => api.listClasses(false) });

  const [form, setForm] = useState({
    sailNumber: initial?.sailNumber ?? '',
    boatName: initial?.boatName ?? '',
    skipperName: initial?.skipperName ?? '',
    crew: initial?.crew ?? '',
    classId: initial?.classId ?? '',
    notes: initial?.notes ?? '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [claim, setClaim] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        boatName: form.boatName,
        skipperName: form.skipperName || undefined,
        crew: form.crew || null,
        classId: form.classId || null,
        notes: form.notes || null,
      };
      if (initial) return { status: 'created' as const, boat: await api.brukerUpdateBaat(initial.id, payload) };
      return api.brukerCreateBaat({ sailNumber: form.sailNumber, ...payload });
    },
    onSuccess: (res) => {
      if (res.status === 'claim') {
        setClaim(true);
        return;
      }
      qc.invalidateQueries({ queryKey: ['mine-baater'] });
      onClose();
    },
    onError: (e: ApiError) => setErr(e.message),
  });

  const remove = useMutation({
    mutationFn: async () => initial && api.brukerRemoveBaat(initial.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mine-baater'] });
      onClose();
    },
  });

  if (claim) {
    return (
      <div className="mb-4 bg-slate-900/60 border border-slate-800 rounded-xl p-4">
        <h2 className="font-semibold mb-2">Båten finnes allerede</h2>
        <p className="text-sm text-slate-400 mb-4">
          Seilnummeret er allerede registrert i klubben. Vi har sendt en forespørsel om å knytte deg
          til båten, som en administrator må godkjenne.
        </p>
        <button onClick={onClose} className="bg-sky-600 hover:bg-sky-500 px-4 py-2 rounded font-semibold">
          OK
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="mb-4 bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3"
    >
      <h2 className="font-semibold">{initial ? 'Rediger båt' : 'Ny båt'}</h2>
      {err && (
        <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">{err}</div>
      )}

      <div>
        <label className="block text-xs text-slate-400 mb-1">Seilnummer</label>
        <input
          value={form.sailNumber}
          onChange={(e) => setForm({ ...form, sailNumber: e.target.value.toUpperCase() })}
          required
          disabled={!!initial}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg font-mono disabled:opacity-60"
        />
        {initial && (
          <p className="text-xs text-slate-500 mt-1">Seilnummer kan ikke endres her.</p>
        )}
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Båtnavn</label>
        <input
          value={form.boatName}
          onChange={(e) => setForm({ ...form, boatName: e.target.value })}
          required
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Skipper</label>
        <input
          value={form.skipperName}
          onChange={(e) => setForm({ ...form, skipperName: e.target.value })}
          placeholder="Ditt navn"
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Mannskap (fri tekst)</label>
        <input
          value={form.crew ?? ''}
          onChange={(e) => setForm({ ...form, crew: e.target.value })}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          placeholder="Ole, Kari, Per"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Klasse</label>
        <select
          value={form.classId ?? ''}
          onChange={(e) => setForm({ ...form, classId: e.target.value })}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        >
          <option value="">— Ingen klasse —</option>
          {(classes.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={save.isPending}
          className="flex-1 bg-sky-600 hover:bg-sky-500 py-3 rounded font-semibold disabled:opacity-60"
        >
          {save.isPending ? 'Lagrer…' : 'Lagre'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded font-semibold"
        >
          Avbryt
        </button>
      </div>
      {initial && (
        <button
          type="button"
          onClick={() => {
            if (confirm(`Fjerne ${initial.boatName} fra dine båter?`)) remove.mutate();
          }}
          className="w-full text-xs text-red-400 py-2"
        >
          Fjern tilknytning
        </button>
      )}
    </form>
  );
}

// --- MINE SEILASER ---

function SeilaserSection() {
  const qc = useQueryClient();
  const seilaser = useQuery({ queryKey: ['mine-seilaser'], queryFn: () => api.brukerSeilaser() });

  const trekk = useMutation({
    mutationFn: (entryId: string) => api.brukerTrekkPaamelding(entryId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mine-seilaser'] }),
  });

  const rows = seilaser.data ?? [];

  return (
    <div className="space-y-5">
      <MeldPaaForm />

      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400 mb-2">Mine seilaser</h2>
        {seilaser.isLoading ? (
          <p className="text-slate-500 text-sm">Laster…</p>
        ) : rows.length === 0 ? (
          <div className="text-center text-slate-500 text-sm p-6 border border-slate-800 rounded-xl">
            Ingen påmeldinger ennå. Meld båten din på en regatta over.
          </div>
        ) : (
          <ul className="divide-y divide-slate-800 border border-slate-800 rounded-xl overflow-hidden">
            {rows.map((s: MySeilas) => (
              <li key={s.entryId} className="p-3 bg-slate-900/30 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <Link to={`/r/${s.raceSlug}`} className="font-semibold truncate hover:text-sky-300">
                    {s.raceName}
                  </Link>
                  <div className="text-xs text-slate-400">
                    {new Date(s.raceStartTime).toLocaleDateString('no-NO')} • {s.sailNumber} {s.boatName}
                  </div>
                </div>
                <div className="text-right text-xs shrink-0">
                  {s.finishNotes ? (
                    <span className="text-amber-300">{s.finishNotes}</span>
                  ) : s.position != null ? (
                    <span className="text-emerald-300 font-semibold">{s.position}. plass</span>
                  ) : s.raceStatus !== 'finished' ? (
                    <button
                      onClick={() => {
                        if (confirm('Trekke påmeldingen?')) trekk.mutate(s.entryId);
                      }}
                      className="text-red-400 hover:text-red-300"
                    >
                      Trekk
                    </button>
                  ) : (
                    <span className="text-slate-500">—</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MeldPaaForm() {
  const qc = useQueryClient();
  const boats = useQuery({ queryKey: ['mine-baater'], queryFn: () => api.brukerBaater() });
  const open = useQuery({ queryKey: ['apne-regattaer'], queryFn: () => api.brukerApneRegattaer() });
  const [raceId, setRaceId] = useState('');
  const [boatId, setBoatId] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const meld = useMutation({
    mutationFn: () => api.brukerMeldPaa(raceId, boatId),
    onSuccess: () => {
      setErr(null);
      setMsg('Båten er påmeldt!');
      setRaceId('');
      setBoatId('');
      qc.invalidateQueries({ queryKey: ['mine-seilaser'] });
    },
    onError: (e: ApiError) => {
      setMsg(null);
      setErr(e.message);
    },
  });

  const myBoats = boats.data ?? [];
  const races = open.data ?? [];

  if (!boats.isLoading && myBoats.length === 0) {
    return (
      <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 text-sm text-slate-400">
        Legg til en båt under «Mine båter» for å melde deg på regattaer.
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (raceId && boatId) meld.mutate();
      }}
      className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 space-y-3"
    >
      <h2 className="font-semibold text-sm">
        Meld på regatta{' '}
        <span className="text-xs font-normal text-slate-500">— også i andre klubber</span>
      </h2>
      {err && (
        <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">{err}</div>
      )}
      {msg && (
        <div className="text-emerald-300 bg-emerald-900/20 border border-emerald-800 rounded p-2 text-sm">
          {msg}
        </div>
      )}
      <div>
        <label className="block text-xs text-slate-400 mb-1">Regatta</label>
        <select
          value={raceId}
          onChange={(e) => setRaceId(e.target.value)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm"
        >
          <option value="">— Velg åpen regatta —</option>
          {races.map((r) => (
            <option key={r.id} value={r.id}>
              {new Date(r.startTime).toLocaleDateString('no-NO')} — {r.name}
              {r.foreningName ? ` (${r.foreningName})` : ''}
            </option>
          ))}
        </select>
        {open.data && races.length === 0 && (
          <p className="text-xs text-slate-500 mt-1">Ingen åpne regattaer akkurat nå.</p>
        )}
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Båt</label>
        <select
          value={boatId}
          onChange={(e) => setBoatId(e.target.value)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm"
        >
          <option value="">— Velg båt —</option>
          {myBoats.map((b) => (
            <option key={b.id} value={b.id}>
              {b.sailNumber} {b.boatName}
              {b.className ? ` (${b.className})` : ''}
            </option>
          ))}
        </select>
      </div>
      <button
        type="submit"
        disabled={meld.isPending || !raceId || !boatId}
        className="w-full bg-sky-600 hover:bg-sky-500 py-2.5 rounded font-semibold disabled:opacity-50"
      >
        {meld.isPending ? 'Melder på…' : 'Meld på'}
      </button>
    </form>
  );
}

// --- INNSTILLINGER ---

function InnstillingerSection({ me }: { me: AppUser }) {
  const qc = useQueryClient();
  const knop = (me.settings?.enheterKnop ?? true) as boolean;

  const save = useMutation({
    mutationFn: (next: boolean) =>
      api.brukerSetInnstillinger({ ...(me.settings ?? {}), enheterKnop: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['konto-me'] }),
  });

  return (
    <div className="space-y-4">
      <ProfilForm me={me} />
      <PassordForm />

      <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <span className="text-sm">Vis fart i knop (av = km/t)</span>
          <input
            type="checkbox"
            checked={knop}
            disabled={save.isPending}
            onChange={(e) => save.mutate(e.target.checked)}
            className="h-5 w-5 accent-sky-500"
          />
        </label>
      </div>

      <p className="text-xs text-slate-500">
        Flere instrument-innstillinger kommer med Racecloud-appen.
      </p>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm';

/** Egne opplysninger: navn og hjemklubb kan endres; e-post er innloggingsnavnet og står fast. */
function ProfilForm({ me }: { me: AppUser }) {
  const qc = useQueryClient();
  const foreninger = useQuery({ queryKey: ['foreninger'], queryFn: () => api.listForeninger() });
  const [fullName, setFullName] = useState(me.fullName);
  const [foreningId, setForeningId] = useState(me.foreningId);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => api.brukerSetProfil({ fullName: fullName.trim(), foreningId }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['konto-me'] });
    },
  });

  const changed = fullName.trim() !== me.fullName || foreningId !== me.foreningId;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 space-y-3"
    >
      <h2 className="font-semibold">Mine opplysninger</h2>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Navn</label>
        <input
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value);
            setSaved(false);
          }}
          required
          maxLength={100}
          autoComplete="name"
          className={inputCls}
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Hjemklubb</label>
        <select
          value={foreningId}
          onChange={(e) => {
            setForeningId(e.target.value);
            setSaved(false);
          }}
          className={inputCls}
        >
          {(foreninger.data ?? []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">E-post (innlogging)</label>
        <div className="px-3 py-2 text-sm text-slate-300 bg-slate-950/50 border border-slate-800 rounded-lg truncate">
          {me.email}
        </div>
        <p className="text-xs text-slate-500 mt-1">E-post kan ikke endres her — kontakt administrator.</p>
      </div>
      <div className="text-xs text-slate-500">Status: {me.status}</div>
      {save.isError && (
        <div className="text-red-300 bg-red-900/30 border border-red-800 rounded-lg p-2 text-sm">
          {(save.error as ApiError).message}
        </div>
      )}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={!changed || !fullName.trim() || save.isPending}
          className="px-4 py-2 bg-sky-600 hover:bg-sky-500 rounded-lg text-sm font-semibold disabled:opacity-50"
        >
          {save.isPending ? 'Lagrer…' : 'Lagre'}
        </button>
        {saved && !changed && <span className="text-sm text-emerald-400">✓ Lagret</span>}
      </div>
    </form>
  );
}

/** Bytt passord (krever nåværende). Andre innlogginger logges ut av serveren. */
function PassordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const save = useMutation({
    mutationFn: () => api.brukerByttPassord(current, next),
    onSuccess: () => {
      setDone(true);
      setCurrent('');
      setNext('');
      setRepeat('');
    },
    onError: (e) => setErr((e as ApiError).message),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setErr(null);
        setDone(false);
        if (next !== repeat) {
          setErr('De nye passordene er ikke like.');
          return;
        }
        save.mutate();
      }}
      className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 space-y-3"
    >
      <h2 className="font-semibold">Bytt passord</h2>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Nåværende passord</label>
        <input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
          autoComplete="current-password"
          className={inputCls}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Nytt passord (minst 8 tegn)</label>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Gjenta nytt passord</label>
          <input
            type="password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            className={inputCls}
          />
        </div>
      </div>
      {err && (
        <div className="text-red-300 bg-red-900/30 border border-red-800 rounded-lg p-2 text-sm">{err}</div>
      )}
      {done && (
        <div className="text-emerald-300 text-sm">
          ✓ Passordet er byttet. Andre enheter (også appen) er logget ut.
        </div>
      )}
      <button
        type="submit"
        disabled={save.isPending}
        className="px-4 py-2 bg-sky-600 hover:bg-sky-500 rounded-lg text-sm font-semibold disabled:opacity-50"
      >
        {save.isPending ? 'Bytter…' : 'Bytt passord'}
      </button>
    </form>
  );
}
